import React, { type ComponentType, forwardRef } from 'react';
import clsx from 'clsx';

interface PrimaryButtonProps {
  text: string;
  state?: 'default' | 'disabled' | 'loading';
  size?: 'md' | 'sm';
  onClick?: () => void;
  icon: ComponentType<React.SVGProps<SVGSVGElement>> | null;
  className?: string;
  type?: 'default' | 'rounded';
  disabled?: boolean;
}

export const PrimaryButton = forwardRef<HTMLButtonElement, PrimaryButtonProps>(
  function PrimaryButton(
    {
      text = 'Primary',
      size = 'md',
      state = 'default',
      icon: Icon,
      onClick,
      className = '',
      type = 'default',
      disabled = false,
      ...rest
    },
    ref,
  ) {
    const isDisabled = disabled ? true : state === 'disabled' || state === 'loading';

    return (
      <button
        ref={ref}
        className={clsx(
          'flex items-center justify-center overflow-hidden outline outline-offset-[-1px] outline-transparent transition-all',
          {
            'gap-2 px-[11px] py-[7px]': size === 'md',
            'gap-1 px-[7px] py-[5px]': size === 'sm',
            'cursor-not-allowed bg-indigo-200': state === 'disabled',
            'cursor-progress': state === 'loading',
            'cursor-pointer bg-indigo-500 hover:bg-indigo-700 focus-visible:bg-indigo-800 active:bg-indigo-800':
              state === 'default' || state === 'loading',
            'h-7 w-7 rounded-full !p-0': type === 'rounded',
            'rounded-md': type === 'default' || state === 'loading',
          },
          className,
        )}
        onClick={onClick}
        disabled={isDisabled}
        {...(rest as React.HTMLAttributes<HTMLButtonElement>)}
      >
        {Icon && (
          <>
            <Icon
              className={clsx('text-white', {
                'h-5 w-5': size === 'md',
                'h-4 w-4': size === 'sm',
                'animate-spin': state === 'loading',
              })}
            />
          </>
        )}

        {text && (
          <span
            className={clsx('text-white', {
              'text-button-md': size === 'md',
              'text-button-sm': size === 'sm',
            })}
          >
            {text}
          </span>
        )}
      </button>
    );
  },
);
